import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useKeycloak } from '@react-keycloak/web';
import { IoClose } from 'react-icons/io5';
import { DesignSystem } from '../../styles/designSystem';
import { createProjectAsset, CreateAssetRequest, getProjectAssetStatuses, AssetStatus } from '../../api/assets';
import { getProjectUsers } from '../../api/projects';

interface AddAssetModalProps {
  projectId: string;
  onClose: () => void;
  onAssetAdded: () => void;
}

interface FormData {
  item: string;
  name: string;
  description: string;
  quantity: number;
  ordered_at: string;
  eta: string;
  tracking_number: string;
  lead_time: string;
  esd: string;
  isd: string;
  selectedStatusId: string;
}

export const AddAssetModal: React.FC<AddAssetModalProps> = ({ projectId, onClose, onAssetAdded }) => {
  const { keycloak: { token, idTokenParsed }, keycloak } = useKeycloak();
  const [isLoading, setIsLoading] = useState(false);
  const [statuses, setStatuses] = useState<AssetStatus[]>([]);
  const [currentUserInProject, setCurrentUserInProject] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    item: '',
    name: '',
    description: '',
    quantity: 1,
    ordered_at: '',
    eta: '',
    tracking_number: '',
    lead_time: '',
    esd: '',
    isd: '',
    selectedStatusId: ''
  });

  // Load current user from project users
  const loadCurrentUserFromProject = async () => {
    if (!token || !projectId || !idTokenParsed) return;
    
    try {
      const response = await getProjectUsers(token, projectId);
      const projectUsers = response.results || [];
      
      // Try to find current user by email (most reliable)
      const currentUserEmail = idTokenParsed.email;
      let foundUser = null;
      
      if (currentUserEmail) {
        foundUser = projectUsers.find(userWithRole => 
          userWithRole.user?.email?.toLowerCase() === currentUserEmail.toLowerCase()
        );
      }
      
      // Fallback: try to match by preferred_username or sub
      if (!foundUser && idTokenParsed.preferred_username) {
        foundUser = projectUsers.find(userWithRole => 
          userWithRole.user?.username?.toLowerCase() === idTokenParsed.preferred_username.toLowerCase()
        );
      }
      
      if (foundUser) {
        setCurrentUserInProject(foundUser.user.id);
        console.log('👤 Current user found in project:', foundUser.user.first_name, foundUser.user.last_name, 'ID:', foundUser.user.id);
      } else {
        console.log('⚠️ Current user not found in project users');
        setCurrentUserInProject(null);
      }
    } catch (error) {
      console.error('Error loading project users:', error);
      setCurrentUserInProject(null);
    }
  };

  // Load asset statuses on mount
  const loadStatuses = async () => {
    if (!token || !projectId) return;
    
    try {
      const response = await getProjectAssetStatuses(token, projectId, keycloak);
      const statusList = response.results || [];
      setStatuses(statusList);
      console.log('📊 Loaded asset statuses for new asset:', statusList.map(s => ({ id: s.id, name: s.name, order: s.status_order })));
      
      // Auto-select default status if not already selected
      if (!formData.selectedStatusId && statusList.length > 0) {
        // Try to find common initial statuses in order of preference
        const defaultStatusNames = [
          'backlog',  // Primary default status
          'planning to purchase',
          'in warehouse', 
          'warehouse',
          'ordered',
          'planning',
          'draft'
        ];
        
        let defaultStatus = null;
        for (const statusName of defaultStatusNames) {
          defaultStatus = statusList.find(s => s.name.toLowerCase().includes(statusName));
          if (defaultStatus) break;
        }
        
        // Fallback: use the first status by order
        if (!defaultStatus) {
          defaultStatus = statusList.sort((a, b) => a.status_order - b.status_order)[0];
        }
        
        if (defaultStatus) {
          setFormData(prev => ({ ...prev, selectedStatusId: defaultStatus.id }));
          console.log('🎯 Auto-selected default status for new asset:', defaultStatus.name, '(Backlog preferred)');
        }
      }
    } catch (error) {
      console.error('Error loading asset statuses:', error);
      // Don't show error to user - we'll use fallback
      setStatuses([]);
    }
  };

  useEffect(() => {
    loadStatuses();
    loadCurrentUserFromProject();
  }, [token, projectId, idTokenParsed]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    setIsLoading(true);
    try {
      // Validate that a status is selected
      if (!formData.selectedStatusId) {
        toast.error('Please select a status for the asset');
        setIsLoading(false);
        return;
      }

      const selectedStatus = statuses.find(s => s.id === formData.selectedStatusId);
      console.log('🎯 Using selected status for new asset:', selectedStatus?.name, 'ID:', formData.selectedStatusId);

      // Convert form data to API format - only include non-empty optional fields
      const cleanData: CreateAssetRequest = {
        project_id: projectId,
        item: formData.item,
        name: formData.name,
        description: formData.description,
        quantity: formData.quantity,
        current_status_id: formData.selectedStatusId, // User-selected status
        current_responsible_person_id: currentUserInProject, // Auto-assign creator as responsible person
        is_installed: false, // Required field - new assets start as not installed
      };

      // Only add optional fields if they have actual values (not empty strings)
      if (formData.ordered_at && formData.ordered_at.trim()) {
        cleanData.ordered_at = formData.ordered_at;
      }
      if (formData.eta && formData.eta.trim()) {
        cleanData.eta = formData.eta;
      }
      if (formData.tracking_number && formData.tracking_number.trim()) {
        cleanData.tracking_number = formData.tracking_number;
      }
      if (formData.lead_time && formData.lead_time.trim()) {
        cleanData.lead_time = formData.lead_time;
      }
      if (formData.esd && formData.esd.trim()) {
        cleanData.esd = formData.esd;
      }
      if (formData.isd && formData.isd.trim()) {
        cleanData.isd = formData.isd;
      }

      console.log('🔧 AddAssetModal: Sending asset data:', cleanData);
      if (currentUserInProject) {
        console.log('👤 Auto-assigning creator as responsible person:', currentUserInProject);
      } else {
        console.log('⚠️ No current user in project - asset will be created without responsible person');
      }

      await createProjectAsset(token, projectId, cleanData);
      toast.success('Asset created successfully');
      onAssetAdded();
      onClose();
    } catch (error) {
      console.error('Error creating asset:', error);
      toast.error('Failed to create asset');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'quantity' ? parseInt(value) || 1 : value
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 
              className="text-[20px] font-semibold tracking-[-0.01em]"
              style={{ 
                color: DesignSystem.colors.primary.text,
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
              }}
            >
              Add New Asset
            </h2>
            <button
              onClick={onClose}
              className="text-[#aaa] hover:text-[#555] transition-colors"
            >
              <IoClose size={24} />
            </button>
          </div>

          {/* Auto-assignment info */}
          {currentUserInProject && (
            <div className="mb-4 p-3 bg-[#f5f5f5] border border-[#eaeaea] rounded-lg">
              <div className="text-sm text-[#171717]">
                <strong>👤 Auto-assignment:</strong> You will be automatically assigned as the responsible person for this asset.
              </div>
            </div>
          )}

          {!currentUserInProject && (
            <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <div className="text-sm text-orange-800">
                <strong>⚠️ Note:</strong> You are not found in the project team. The asset will be created without a responsible person.
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {/* Item */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Item Code *
                </label>
                <input
                  type="text"
                  name="item"
                  value={formData.item}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                  placeholder="e.g., VEDGE-1000-AC-K9"
                />
              </div>

              {/* Name */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Asset Name *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                  placeholder="e.g., Cisco Viptela Vedge-1000"
                />
              </div>
            </div>

            {/* Description */}
            <div>
              <label 
                className="block text-sm font-medium mb-1"
                style={{ 
                  color: DesignSystem.colors.primary.text,
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                }}
              >
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                style={{ 
                  borderColor: DesignSystem.colors.border.gray200,
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                }}
                placeholder="Asset description..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Status */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Status *
                </label>
                <select
                  name="selectedStatusId"
                  value={formData.selectedStatusId}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  <option value="">Select status...</option>
                  {statuses
                    .sort((a, b) => a.status_order - b.status_order)
                    .map(status => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                        {status.requires_responsible_person && currentUserInProject ? ' (👤 You will be assigned)' : ''}
                        {status.requires_responsible_person && !currentUserInProject ? ' (⚠️ Requires responsible person)' : ''}
                      </option>
                    ))
                  }
                </select>
                {/* Status requirements info */}
                {formData.selectedStatusId && (
                  (() => {
                    const selectedStatus = statuses.find(s => s.id === formData.selectedStatusId);
                    if (selectedStatus && selectedStatus.requires_responsible_person) {
                      return (
                        <div className={`mt-2 p-2 text-xs rounded ${
                          currentUserInProject 
                            ? 'bg-green-50 text-green-700 border border-green-200' 
                            : 'bg-red-50 text-red-700 border border-red-200'
                        }`}>
                          {currentUserInProject 
                            ? '✅ This status requires a responsible person. You will be automatically assigned.'
                            : '❌ This status requires a responsible person, but you are not in the project team.'
                          }
                        </div>
                      );
                    }
                    return null;
                  })()
                )}
              </div>

              {/* Quantity */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Quantity *
                </label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleInputChange}
                  min="1"
                  required
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                />
              </div>

              {/* Tracking Number */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Tracking Number
                </label>
                <input
                  type="text"
                  name="tracking_number"
                  value={formData.tracking_number}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                  placeholder="Tracking number..."
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Ordered At */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Ordered At
                </label>
                <input
                  type="datetime-local"
                  name="ordered_at"
                  value={formData.ordered_at}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                />
              </div>

              {/* ETA */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  ETA
                </label>
                <input
                  type="datetime-local"
                  name="eta"
                  value={formData.eta}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Lead Time */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  Lead Time
                </label>
                <input
                  type="text"
                  name="lead_time"
                  value={formData.lead_time}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                  placeholder="e.g., 2 weeks"
                />
              </div>

              {/* ESD */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  ESD
                </label>
                <input
                  type="text"
                  name="esd"
                  value={formData.esd}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                  placeholder="ESD..."
                />
              </div>

              {/* ISD */}
              <div>
                <label 
                  className="block text-sm font-medium mb-1"
                  style={{ 
                    color: DesignSystem.colors.primary.text,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                >
                  ISD
                </label>
                <input
                  type="text"
                  name="isd"
                  value={formData.isd}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0"
                  style={{ 
                    borderColor: DesignSystem.colors.border.gray200,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                  }}
                  placeholder="ISD..."
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border rounded-lg hover:bg-[#fafafa] transition-colors"
                style={{ 
                  borderColor: DesignSystem.colors.border.gray200,
                  color: DesignSystem.colors.primary.textSecondary,
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50"
                style={{ 
                  backgroundColor: DesignSystem.colors.brand[600],
                  fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, sans-serif' 
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = DesignSystem.colors.brand[700];
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                    e.currentTarget.style.backgroundColor = DesignSystem.colors.brand[600];
                  }
                }}
              >
                {isLoading ? 'Creating...' : 'Create Asset'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}; 